// Formato de fechas/horas para MOSTRAR en la zona horaria de la sucursal/tenant.
//
// Regla (espeja el backend, fuente única de verdad tzutils.py): la interpretación de "qué día
// es" vive en el backend; aquí SOLO se formatea un instante UTC para mostrarlo en la zona local
// correcta. La zona activa se resuelve: explícita → activa (sucursal/tenant) → America/Mexico_City.
//
// Nunca se modifican datos: los timestamps siguen siendo UTC; solo cambia su presentación.

const DEFAULT_TZ = "America/Mexico_City";
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

let _activeTz = null;

export function setActiveTimeZone(tz) {
  if (tz && typeof tz === "string") {
    _activeTz = tz;
    try { localStorage.setItem("pap_tz", tz); } catch { /* ignore */ }
  }
}

export function getActiveTimeZone() {
  if (_activeTz) return _activeTz;
  try {
    const s = localStorage.getItem("pap_tz");
    if (s) { _activeTz = s; return s; }
  } catch { /* ignore */ }
  return DEFAULT_TZ;
}

function resolveTz(tz) {
  return (tz && typeof tz === "string") ? tz : getActiveTimeZone();
}

function toDate(value) {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// 'YYYY-MM-DD' (día ya calculado por el backend en hora local) → se muestra como fecha de
// calendario tal cual, SIN reconvertir por zona horaria (evita corrimientos de ±1 día).
function fmtDateOnly(ymd, opts) {
  const [y, m, dd] = ymd.split("-").map(Number);
  const d = new Date(y, (m || 1) - 1, dd || 1);
  const base = (opts && Object.keys(opts).length)
    ? { ...opts } : { day: "2-digit", month: "2-digit", year: "numeric" };
  delete base.hour; delete base.minute; delete base.second; delete base.timeZone;
  try { return new Intl.DateTimeFormat("es-MX", base).format(d); } catch { return ymd; }
}

const DT_DEFAULT = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };

export function fmtDateTime(value, opts = {}, tz) {
  if (typeof value === "string" && DATE_ONLY.test(value)) return fmtDateOnly(value, opts);
  const d = toDate(value);
  if (!d) return "—";
  const base = (opts && Object.keys(opts).length) ? opts : DT_DEFAULT;
  try { return new Intl.DateTimeFormat("es-MX", { timeZone: resolveTz(tz), ...base }).format(d); }
  catch { return "—"; }
}

export function fmtDate(value, opts = {}, tz) {
  if (typeof value === "string" && DATE_ONLY.test(value)) return fmtDateOnly(value, opts);
  const d = toDate(value);
  if (!d) return "—";
  const base = (opts && Object.keys(opts).length) ? opts : { day: "2-digit", month: "2-digit", year: "numeric" };
  try { return new Intl.DateTimeFormat("es-MX", { timeZone: resolveTz(tz), ...base }).format(d); }
  catch { return "—"; }
}

export function fmtTime(value, opts = {}, tz) {
  const d = toDate(value);
  if (!d) return "—";
  const base = (opts && Object.keys(opts).length) ? opts : { hour: "2-digit", minute: "2-digit" };
  try { return new Intl.DateTimeFormat("es-MX", { timeZone: resolveTz(tz), ...base }).format(d); }
  catch { return "—"; }
}

// ¿"value" (instante UTC) cae HOY en la zona activa? (comparación de día local, no del dispositivo)
export function esHoyLocal(value, tz) {
  const d = toDate(value);
  if (!d) return false;
  const z = resolveTz(tz);
  const f = (x) => new Intl.DateTimeFormat("en-CA", { timeZone: z, year: "numeric", month: "2-digit", day: "2-digit" }).format(x);
  return f(d) === f(new Date());
}

// Día de la semana (0=Dom … 6=Sáb) en la zona activa (para horarios "de hoy" en tickets).
export function getLocalWeekday(tz) {
  const z = resolveTz(tz);
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: z, weekday: "short" }).format(new Date());
  return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[wd] ?? new Date().getDay();
}
